/**
 * The school's identity (Ajustes → Datos del centro): names, web, contact, legal text and
 * the logo used in emails. Loaded before anything is drawn (login and the public enrollment
 * form included), so no screen has a school's name written in its code.
 */
const SchoolService = {
    profile: {},

    async load() {
        try {
            const response = await fetch('/api/public/school', {cache: 'no-store'});
            const result = await response.json().catch(() => null);
            if (response.ok && result?.school && typeof result.school === 'object') this.profile = result.school;
        } catch {
            // Without the data, screens show the product name (SICAF) instead.
        }
        return this.profile;
    },

    get(field) { return typeof this.profile[field] === 'string' ? this.profile[field] : ''; },
    // Full name of the school.
    name() { return this.get('name') || this.shortName(); },
    // Siglas for labels; the full name or the product name if there are none.
    shortName() { return this.get('shortName') || this.get('name') || 'SICAF'; },
    // "Comunidad <siglas>", "Asistente <siglas>"…
    label(prefix) { return `${prefix} ${this.shortName()}`; },

    // An image from Ajustes → Imagen that the app may show (uploaded, or https), or ''.
    image(slot) {
        const url = this.get(slot) || '';
        return /^https:\/\/[^\s"'<>]+$/.test(url) || /^\/school-assets\/[0-9a-f-]{36}\.(png|jpg|webp)$/.test(url) ? url : '';
    },

    // The logo as an <img>, or the school's short name as text when none has been uploaded.
    logoHtml(slots, attrs = '') {
        const url = [].concat(slots).map(slot => this.image(slot)).find(Boolean) || '';
        const escape = value => String(value ?? '').replace(/[&<>"']/g, c => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'}[c]));
        if (url) return `<img src="${escape(url)}" alt="${escape(this.label('Logo'))}" ${attrs}>`;
        // The stand-in keeps the logo's class, so it sits where the logo would.
        const withClass = /class="/.test(attrs) ? attrs.replace('class="', 'class="school-logo-text ') : `class="school-logo-text" ${attrs}`;
        return `<span ${withClass}>${escape(this.shortName())}</span>`;
    },

    // Uploads a logo (Ajustes → Imagen); it is saved at once and replaces the current one.
    async uploadLogo(slot, file) {
        const body = new FormData();
        body.append('logo', file);
        const response = await fetch('/api/settings/school/logo/' + encodeURIComponent(slot), {
            method: 'POST', headers: {'X-SICAF-CSRF': AuthManager.getCsrfToken()}, body
        });
        const result = await response.json().catch(() => null);
        if (!response.ok || !result?.success) throw new Error(result?.error || 'No se pudo subir el logo.');
        this.profile = result.school;
        return this.profile;
    },

    // Uploads the PDF of the privacy policy or the school rules; saved at once.
    async uploadDocument(slot, file) {
        const body = new FormData();
        body.append('document', file);
        const response = await fetch('/api/settings/school/document/' + encodeURIComponent(slot), {
            method: 'POST', headers: {'X-SICAF-CSRF': AuthManager.getCsrfToken()}, body
        });
        const result = await response.json().catch(() => null);
        if (!response.ok || !result?.success) throw new Error(result?.error || 'No se pudo subir el PDF.');
        this.profile = result.school;
        return this.profile;
    },

    async save(data) {
        const response = await fetch('/api/settings/school', {
            method: 'PUT', headers: {'Content-Type': 'application/json', 'X-SICAF-CSRF': AuthManager.getCsrfToken()}, body: JSON.stringify(data)
        });
        const result = await response.json().catch(() => null);
        if (!response.ok || !result?.success) throw new Error(result?.error || 'No se pudieron guardar los datos del centro.');
        this.profile = result.school;
        return this.profile;
    }
};

window.SchoolService = SchoolService;
